export interface RecoveryScope {
    connection: number;
    vessel: number;
    session: number;
}

export type RecoveryRunOutcome = 'completed' | 'failed' | 'superseded';

/**
 * Serializes reconnect work and makes an already queued/running scope obsolete
 * as soon as a newer reconnect, vessel selection, or session is observed.
 */
export class ConnectionRecoveryCoordinator {
    private latestRunId = 0;
    private tail: Promise<RecoveryRunOutcome> = Promise.resolve('superseded');

    public run(
        scope: RecoveryScope,
        isCurrent: (scope: RecoveryScope) => boolean,
        recover: () => Promise<void>
    ): Promise<RecoveryRunOutcome> {
        const runId = ++this.latestRunId;
        const previousRun = this.tail.catch(() => 'failed' as RecoveryRunOutcome);

        this.tail = previousRun.then(async () => {
            if (runId !== this.latestRunId || !isCurrent(scope)) {
                return 'superseded';
            }

            try {
                await recover();
                return runId === this.latestRunId && isCurrent(scope) ? 'completed' : 'superseded';
            } catch {
                return runId === this.latestRunId && isCurrent(scope) ? 'failed' : 'superseded';
            }
        });

        return this.tail;
    }

    public invalidate(): void {
        this.latestRunId += 1;
    }
}
